/**
 * Link BIM Tool
 *
 * Links an external BIM-family model document (.rvt/.yap/.json) into the
 * project as a live reference. A wrapper element (the linked-model footprint)
 * is created in the BIM model and bound to the LinkManager registry entry,
 * which is what the Manage Links dialog lists and controls.
 */

import { v4 as uuidv4 } from 'uuid';
import { LinkElement, LinkGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { LinkManager } from '../core/LinkManager';
import { ThreeEvent } from '@react-three/fiber';
import i18n from '../i18n';

export class LinkBimTool extends BaseTool {
    id = 'link_bim';
    name = 'Link BIM';
    cursor = 'alias';

    private linkManager: LinkManager;

    constructor(linkManager: LinkManager) {
        super();
        this.linkManager = linkManager;
    }

    public executeLink(filePath: string, size = 0) {
        // 1. Create Link Instance
        const linkInstance = this.linkManager.linkFile('BIM', filePath, {
            positioning: 'OriginToOrigin',
            size
        });

        // 2. Create Wrapper Element
        this.createLinkElement(linkInstance);
        return linkInstance;
    }

    onActivate(): void {
        // Dialog-driven command: BIM opens a file dialog for Link BIM.
        void this.pickFile('.rvt,.rfa,.yap,.json').then((file) => {
            if (file) {
                this.executeLink(file.name, file.size);
                this.notify(i18n.t('tools.notify.bimLinked', { fileName: file.name }), 'success');
            }
            this.exitToSelect();
        });
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void {
        // Dialog-driven command: nothing to place interactively.
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private createLinkElement(link: any) {
        if (!this.context) return;

        const geometryData: LinkGeometryData = {
            linkId: link.id,
            origin: [0, 0, 0],
            scale: [1, 1, 1],
            rotation: [0, 0, 0],
            boxSize: [100, 100, 50] // Huge box representing the linked building
        };

        const linkElement: LinkElement = {
            id: uuidv4(),
            category: 'BIM Links',
            type: 'Linked BIM Model: ' + link.name,
            parameters: {
                'File Path': link.path,
                'Status': link.status,
                'Shared Site': 'Internal'
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(linkElement);
        // Bind the wrapper element to the registry entry so Manage Links can
        // hide/unhide/reload/remove the exact scene object.
        this.linkManager.setDocumentId(link.id, linkElement.id);
        console.log(`BIM Link Created: ${linkElement.id}`);
    }
}
